import axios from "axios";
import { httpClient } from "./api/client";
import { log } from "./logger";

export function excelSerialToJsDate(serial: number): Date {
  if (typeof serial !== "number" || isNaN(serial)) {
    throw new Error("Invalid input: must be a number");
  }

  // Excel dates range from 0 to 2958465 (1900-01-01 to 9999-12-31)
  if (serial < 0 || serial > 2958465) {
    throw new Error("Date out of range: must be between 0 and 2958465");
  }

  const msPerDay = 86400000;
  const excelEpoch = Date.UTC(1899, 11, 30);
  const leapBugCutoff = 61;

  const adjusted = serial < leapBugCutoff ? serial - 1 : serial;
  const date = new Date(excelEpoch + adjusted * msPerDay);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid resulting date");
  }

  return date;
}

export async function getWorkbookFile(): Promise<Blob> {
  try {
    await Office.onReady();
    log.info("Starting workbook upload");

    const getFileAsync = (fileType: Office.FileType, options: Office.GetFileOptions): Promise<Office.File> => {
      return new Promise((resolve, reject) => {
        Office.context.document.getFileAsync(fileType, options, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(result.error);
          }
        });
      });
    };

    const getFileSlices = (file: Office.File): Promise<any[]> => {
      return new Promise((resolve, reject) => {
        const slices: any[] = [];
        let sliceCounter = 0;

        const getSlice = () => {
          file.getSliceAsync(sliceCounter, (sliceResult) => {
            if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
              slices.push(sliceResult.value.data);
              sliceCounter++;
              if (sliceCounter < file.sliceCount) {
                getSlice();
              } else {
                resolve(slices);
              }
            } else {
              reject(sliceResult.error);
            }
          });
        };
        getSlice();
      });
    };

    const myFile = await getFileAsync(Office.FileType.Compressed, {
      sliceSize: 4 * 1024 * 1024,
    });
    const slices = await getFileSlices(myFile);
    log.info("All slices received for workbook upload.");

    // Normalize all slices to Uint8Array
    const normalizedSlices = slices.map((slice) => {
      if (typeof slice === "string") {
        const binaryString = atob(slice);
        return Uint8Array.from(Array.from(binaryString, (char) => char.charCodeAt(0)));
      }
      return new Uint8Array(slice); // already a binary slice
    });

    // Merge all slices into one Uint8Array
    const totalLength = normalizedSlices.reduce((sum, slice) => sum + slice.length, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;
    for (const slice of normalizedSlices) {
      merged.set(slice, offset);
      offset += slice.length;
    }

    await myFile.closeAsync();

    // Return the final Blob
    return new Blob([merged], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  } catch (error) {
    log.error("Failed to upload workbook:", error);
    throw error;
  }
}

export async function sendWorkbookToBackend(
  fileData: Blob,
  fileName: string
): Promise<any> {
  const formData = new FormData();
  formData.append("file", fileData, fileName);

  try {
    const response = await httpClient.post("/file/upload", formData, {
      withCredentials: true,
    });

    console.log("Workbook upload successful:", response);
    return response.data;
  } catch (error: any) {
    console.error("Workbook upload failed:", error);

    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return null;
    }

    return null;
  }
}

export async function sendDataToBackend(dimensions: any) {
  const data = {
    non_public: true,
    access_group: "AlphaScript Test",
    uun: "Public Filings",
    reason: "",
    dimensions,
  };

  console.log(data);

  try {
    const response = await httpClient.post("/user-uploads/initial-inputs", data, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log(response);
    openSuccessPopup();
  } catch (error: any) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response?.status === 409) {
      openAlreadyExistsPopup(data);
    }
  }
}

export function openAlreadyExistsPopup(data: any) {
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}upload-already-exists-popup.html`;

  Office.context.ui.displayDialogAsync(
    popupUrl,
    { height: 40, width: 40, displayInIframe: true },
    (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        log.error("Failed to open already exists dialog:", result.error.message);
        return;
      }

      const popupDialog = result.value;

      popupDialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (args: any) => {
          log.info("Message received from already exists popup:", args.message);

          if (args.message === "overwrite") {
            try {
              const response = await httpClient.post(
                "/user-uploads/initial-inputs",
                data,
                {
                  params: { overwrite: true },
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );

              console.log("Overwrite successful:", response);
              popupDialog.close();

              setTimeout(() => {
                openSuccessPopup();
              }, 1000);
            } catch (error: any) {
              console.error("Overwrite failed:", error);

              if (axios.isAxiosError(error) && error.response) {
                log.error(
                  "Overwrite request failed:",
                  error.response.status,
                  error.response.data
                );
              }

              popupDialog.close();
            }
          } else if (args.message === "cancel") {
            popupDialog.close();
          }
        }
      );
    }
  );
}

export function openSuccessPopup() {
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}upload-success-popup.html`;

  Office.context.ui.displayDialogAsync(popupUrl, { height: 40, width: 40, displayInIframe: true }, (result) => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open success dialog:", result.error.message);
    } else {
      const popupDialog = result.value;
      popupDialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
        log.info("Message received from success popup:", args.message);

        if (args.message === "close") {
          popupDialog.close();
        }
      });
    }
  });
}

export function openWorkbookExistsPopup(fileData: Blob, fileName: string) {
  const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
  const popupUrl = `${frontendUrl}upload-already-exists-popup.html`;

  Office.context.ui.displayDialogAsync(popupUrl, { height: 40, width: 40, displayInIframe: true }, (result) => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      log.error("Failed to open already exists dialog:", result.error.message);
    } else {
      const popupDialog = result.value;
      popupDialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
        log.info("Message received from already exists popup:", args.message);

        if (args.message === "overwrite") {
          const base_url = process.env.API_URL;
          const path = "/file/upload";
          const url = `${base_url}${path}?overwrite=true`;

          const formData = new FormData();
          formData.append("file", fileData, fileName);
          formData.append("non_public", "true");
          formData.append("access_group", "AlphaScript Test");
          formData.append("uun", "Public Filings");
          formData.append("reason", "");

          axios
            .post(url, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              console.log("Overwrite successful:", response);
              popupDialog.close();
              setTimeout(() => {
                openSuccessPopup();
              }, 1000);
            })
            .catch((error) => {
              console.error("Overwrite failed:", error);
              popupDialog.close();
            });
        } else if (args.message === "cancel") {
          popupDialog.close();
        }
      });
    }
  });
}

export async function uploadDataToBackend(event?: Office.AddinCommands.Event) {
  const workbookFile = await getWorkbookFile();
  const res = await sendWorkbookToBackend(workbookFile, "capitalizationTemplate.xlsm");
  let templateUrl = "";
  if (res) {
    templateUrl = res?.data?.downloadUrl;
  }

  if (templateUrl == "" || templateUrl == null) {
    log.error("Template URL not found");
    event.completed();
    return;
  }

  Excel.run(async (context) => {
    try {
      // Try to get the "Initial Inputs" worksheet
      let worksheet: Excel.Worksheet;
      try {
        worksheet = context.workbook.worksheets.getItem("Initial Inputs");
      } catch (error) {
        log.error("Worksheet 'Initial Inputs' does not exist");
        event.completed();
        return;
      }

      const dataRow = [
        { name: "Data Source Information::Primary Data Source", cell: "E6" },
        { name: "Data Source Information::Filing Date", cell: "E9", type: "date" },
        { name: "Data Source Information::Filing Type", cell: "E10" },
        { name: "Data Source Information::Filing Link", cell: "E11" },
        { name: "Data Source Information::Period End", cell: "E14" },
        { name: "Data Source Information::Period End Date", cell: "E15", type: "date" },
        { name: "Data Source Information::Financial Year", cell: "E16" },
        { name: "Data Source Information::Financial Year Date", cell: "E17", type: "date" },
        { name: "Data Source Information::Financial Statement Currency", cell: "E20" },
        { name: "Data Source Information::Numerical Scale::Share Counts", cell: "E22" },
        { name: "Data Source Information::Numerical Scale::Currency Values", cell: "E23" },
        { name: "Data Source Information::Numerical Scale::Share Price", cell: "E24" },
        { name: "Data Source Information::Comments", cell: "D27" },

        { name: "Data Source Information::Exchange", cell: "E34" },
        { name: "Data Source Information::Ticker", cell: "E35" },
        { name: "Data Source Information::Company Name", cell: "E36" },
        { name: "Data Source Information::Data Source", cell: "E39" },

        { name: "Data Source Information::As of Date", cell: "E44", type: "date" },
      ];

      // Create the dimensions array
      const dimensions: { name: string; value: any }[] = [];
      dimensions.push({
        name: "__System::Template File::Download Link".trim(),
        value: templateUrl,
      });

      // Read values from each row (D and E columns)
      for (const row of dataRow) {
        const cell = worksheet.getRange(row.cell);

        cell.load("values");
        await context.sync();

        let value = cell.values[0][0];
        if (row.type === "date") {
          try {
            const date = excelSerialToJsDate(value);
            value = date.toISOString().split("T")[0];
          } catch (error) {
            log.error(`Error converting Excel date for ${row.name}:`, error);
            // Keep original value if conversion fails
          }
        }

        // special case for comments: Do not save the placeholder value
        if (row.name === "Data Source Information::Comments") {
          value = value !== "Add any context to the data regarding source." ? value : "";
        }

        dimensions.push({
          name: row.name.trim(),
          value: value,
        });
      }

      sendDataToBackend(dimensions);
      event.completed();
    } catch (error) {
      log.error("Error reading cell values:", error);
      event.completed();
    }
  });
}